import { apiFetch } from './client';
import { putToPresignedUrl } from './uploads';

function buildQuery(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    usp.set(key, value);
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function normalizeProduct(raw) {
  const dims = raw.dimensions || {};
  const nutrition = raw.nutrition || {};
  return {
    id: raw.item_id || raw.id,
    name: raw.name || '',
    sku: raw.sku || '',
    barcode: raw.barcode || '',
    barcodeType: raw.barcode_type || 'EAN13',
    category: raw.category_id || raw.category || '',
    brand: raw.brand || '',
    description: raw.description || '',
    unit: raw.unit || 'ea',
    // Missing sold_by is Pack. Do not infer from unit.
    soldBy: String(raw.sold_by || '').toUpperCase() === 'WEIGHT' ? 'WEIGHT' : 'EACH',
    price: raw.selling_price ?? 0,
    mrp: raw.mrp ?? raw.selling_price ?? 0,
    costPrice: raw.cost_price ?? 0,
    stock: raw.stock_qty ?? 0,
    itemType: raw.item_type === 'food' || raw.item_type === 'general'
      ? raw.item_type
      : (['serving_size', 'energy_kcal', 'protein_g', 'carbs_g', 'fat_g'].some((key) => {
        const value = nutrition[key];
        return value !== undefined && value !== null && value !== '';
      }) ? 'food' : 'general'),
    lowStockThreshold: raw.reorder_level ?? 0,
    minOrderQty: raw.min_order_qty ?? (String(raw.sold_by || '').toUpperCase() === 'WEIGHT' ? 0.001 : 1),
    maxOrderQty: raw.max_order_qty ?? 10,
    qtyStep: raw.qty_step ?? (String(raw.sold_by || '').toUpperCase() === 'WEIGHT' ? 0.001 : 1),
    netQty: raw.net_qty ?? '',
    netQtyUnit: raw.net_qty_unit || 'kg',
    packCount: raw.pack_count ?? 1,
    dimLength: dims.length ?? '',
    dimWidth: dims.width ?? '',
    dimHeight: dims.height ?? '',
    dimUnit: dims.unit || 'cm',
    nutritionServingSize: nutrition.serving_size || '',
    nutritionEnergyKcal: nutrition.energy_kcal ?? '',
    nutritionProteinG: nutrition.protein_g ?? '',
    nutritionCarbsG: nutrition.carbs_g ?? '',
    nutritionFatG: nutrition.fat_g ?? '',
    status: raw.status || 'ACTIVE',
    inStock: raw.in_stock,
    catalogComplete: raw.catalog_complete !== false,
    onboardSource: raw.onboard_source || null,
    hsnCode: raw.hsn || '',
    gstRate: raw.gst_rate ?? 0,
    images: (raw.image_urls || []).map((url) => ({ id: url, url })),
    updatedAt: raw.updated_at,
    createdAt: raw.created_at,
  };
}

function toInventoryPayload(data) {
  const hasDims = [data.dimLength, data.dimWidth, data.dimHeight].some((v) => v !== undefined && v !== '');
  const hasNutrition = [
    data.nutritionServingSize, data.nutritionEnergyKcal, data.nutritionProteinG, data.nutritionCarbsG, data.nutritionFatG,
  ].some((v) => v !== undefined && v !== '');

  const payload = {
    name: data.name,
    sku: data.sku || undefined,
    category: data.category || undefined,
    status: data.status || undefined,
    unit: data.unit || undefined,
    sold_by: data.soldBy === 'WEIGHT' || data.sold_by === 'WEIGHT' ? 'WEIGHT' : (data.soldBy || data.sold_by ? 'EACH' : undefined),
    brand: data.brand || undefined,
    description: data.description || undefined,
    cost_price: data.costPrice !== undefined && data.costPrice !== '' ? Number(data.costPrice) : undefined,
    selling_price: data.price !== undefined && data.price !== '' ? Number(data.price) : undefined,
    mrp: data.mrp !== undefined && data.mrp !== '' ? Number(data.mrp) : undefined,
    stock_qty: data.stock !== undefined && data.stock !== '' ? Number(data.stock) : undefined,
    reorder_level: data.lowStockThreshold !== undefined && data.lowStockThreshold !== '' ? Number(data.lowStockThreshold) : undefined,
    min_order_qty: data.minOrderQty !== undefined && data.minOrderQty !== '' ? Number(data.minOrderQty) : undefined,
    max_order_qty: data.maxOrderQty !== undefined && data.maxOrderQty !== '' ? Number(data.maxOrderQty) : undefined,
    qty_step: data.qtyStep !== undefined && data.qtyStep !== '' ? Number(data.qtyStep) : undefined,
    barcode: data.barcode || undefined,
    barcode_type: data.barcodeType || undefined,
    hsn: data.hsnCode || undefined,
    gst_rate: data.gstRate !== undefined && data.gstRate !== '' ? Number(data.gstRate) : undefined,
    net_qty: data.netQty !== undefined && data.netQty !== '' ? Number(data.netQty) : undefined,
    net_qty_unit: data.netQtyUnit || undefined,
    pack_count: data.packCount !== undefined && data.packCount !== '' ? Number(data.packCount) : undefined,
    dimensions: hasDims ? {
      length: Number(data.dimLength) || 0,
      width: Number(data.dimWidth) || 0,
      height: Number(data.dimHeight) || 0,
      unit: data.dimUnit || 'cm',
    } : undefined,
    item_type: data.itemType === 'food' || data.itemType === 'general' ? data.itemType : undefined,
    // General clears nutrition so the product does not reopen as food.
    nutrition: data.itemType === 'general' ? null : (hasNutrition ? {
      serving_size: data.nutritionServingSize || undefined,
      energy_kcal: data.nutritionEnergyKcal !== '' ? Number(data.nutritionEnergyKcal) : undefined,
      protein_g: data.nutritionProteinG !== '' ? Number(data.nutritionProteinG) : undefined,
      carbs_g: data.nutritionCarbsG !== '' ? Number(data.nutritionCarbsG) : undefined,
      fat_g: data.nutritionFatG !== '' ? Number(data.nutritionFatG) : undefined,
    } : undefined),
    image_url: data.localFile ? undefined : (data.imageUrl || data.image_url || undefined),
    image_keys: Array.isArray(data.imageKeys) && data.imageKeys.length ? data.imageKeys : undefined,
  };
  Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);
  return payload;
}

export async function bulkImportProducts(items, token, { dryRun = false, importImages = true } = {}) {
  const formattedItems = items.map((item) => {
    const payload = toInventoryPayload(item);
    if (!item.localFile && item.imageUrl) {
      payload.image_url = item.imageUrl;
    }
    return payload;
  });

  const body = {
    items: formattedItems,
    dry_run: Boolean(dryRun),
    import_images: Boolean(importImages),
  };

  return apiFetch('/inventory/bulk-import', {
    method: 'POST',
    token,
    body,
  });
}

function parseChunkResponse(raw, chunk) {
  if (!raw) {
    return chunk.map((item) => ({ item, ok: false, error: 'No response from bulk import service.' }));
  }
  const resultsList = raw.results || raw.items || (Array.isArray(raw) ? raw : null);
  if (Array.isArray(resultsList)) {
    return chunk.map((item, idx) => {
      const res = resultsList.find((r) => r.index === idx) || resultsList[idx];
      if (!res) return { item, ok: true, itemId: item.id };
      const ok = res.ok !== false && res.status !== 'error' && !res.error && (!res.errors || !res.errors.length);
      const errors = res.errors || (res.error ? [res.error] : []);
      const rawError = errors.join('; ') || res.error || (ok ? null : 'Failed to import row.');
      return {
        item,
        ok,
        itemId: res.item_id || res.id || res.item?.item_id || res.item?.id || item.id,
        status: res.status,
        error: rawError,
      };
    });
  }
  return chunk.map((item) => ({ item, ok: true, itemId: item.id }));
}

export function normalizeLot(raw) {
  if (!raw) return null;
  return {
    lotId: raw.lot_id || raw.lotId,
    qty: Number(raw.qty) || 0,
    sellingPrice: raw.selling_price ?? raw.sellingPrice ?? 0,
    mrp: raw.mrp ?? null,
    status: raw.status || 'OPEN',
    receivedAt: raw.received_at || raw.receivedAt || null,
    source: raw.source || null,
    costPrice: raw.cost_price ?? raw.costPrice ?? null,
  };
}

/**
 * Direct catalog barcode lookup via DynamoDB barcode registry (pk: BARCODE#{barcode}).
 * Handled by get_by_barcode in lambda_function.py.
 * Method: GET /inventory/by-barcode/{barcode}
 * Returns product item ({ item, ...item }) if found (200), null if not found (404).
 * Throws on 400 (barcode_required) or other API errors.
 */
export async function getByBarcode(barcode, token) {
  const cleaned = String(barcode || '').trim();
  if (!cleaned) {
    const err = new Error('barcode_required');
    err.code = 'barcode_required';
    err.status = 400;
    throw err;
  }

  try {
    const raw = await apiFetch(`/inventory/by-barcode/${encodeURIComponent(cleaned)}`, { token });
    if (!raw) return null;
    const item = normalizeProduct(raw.item || raw);
    return { item, ...item };
  } catch (err) {
    if (err.status === 404 || err.code === 'not_found') {
      return null;
    }
    throw err;
  }
}

/** Full Ops barcode lookup — includes lots when backend has lot pricing live. */
export async function lookupByBarcode(barcode, token) {
  const raw = await apiFetch(`/inventory/by-barcode/${encodeURIComponent(barcode)}`, { token });
  const item = normalizeProduct(raw.item || raw);
  const hasLotsKey = Array.isArray(raw.lots);
  const lots = hasLotsKey ? raw.lots.map(normalizeLot).filter(Boolean) : null;
  return {
    item,
    lots,
    lotPickRequired: Boolean(raw.lot_pick_required),
    priceMin: raw.price_min ?? null,
    priceMax: raw.price_max ?? null,
    hasLots: hasLotsKey,
  };
}

/** @deprecated Prefer getByBarcode or lookupByBarcode when lot pricing is needed. */
export async function getProductByBarcode(barcode, token) {
  const res = await getByBarcode(barcode, token);
  return res?.item || null;
}

export async function listLots(id, token, { includeClosed = false } = {}) {
  const query = buildQuery({ include_closed: includeClosed ? 1 : 0 });
  const raw = await apiFetch(`/inventory/${encodeURIComponent(id)}/lots${query}`, { token });
  const lots = Array.isArray(raw?.lots) ? raw.lots.map(normalizeLot).filter(Boolean) : [];
  return { itemId: raw?.item_id || id, lots };
}

/**
 * Product insight card (B22). Qty and money stay as JSON numbers.
 * from/to are sent only for period=custom.
 */
export async function getProductInsight(itemId, token, {
  period = '30d',
  channel = 'ALL',
  from,
  to,
  limit,
  cursor,
} = {}) {
  const query = buildQuery({
    period,
    channel,
    from: period === 'custom' ? from : undefined,
    to: period === 'custom' ? to : undefined,
    limit,
    cursor,
  });
  return apiFetch(`/inventory/${encodeURIComponent(itemId)}/insight${query}`, { token });
}

export const STOCK_MOVE_ERRORS = {
  lot_id_required: 'Select which lot this move applies to.',
  lot_mismatch: 'That lot is invalid or empty — refresh lots and try again.',
  selling_price_required: 'Enter a sell price for this stock-in.',
  insufficient_stock: 'Not enough stock on that lot.',
  stock_conflict: 'Stock changed while saving — try again.',
  id_and_positive_qty_required: 'Enter a valid quantity greater than zero.',
  id_qty_reason_required: 'Quantity and reason are required.',
  id_and_notes_required: 'Notes are required for this adjustment.',
  no_change: 'No change to save — adjust the quantity first.',
  item_not_found: 'Product not found.',
  not_found: 'Not found.',
  forbidden: 'You do not have permission to move stock.',
};

const BARCODE_LOOKUP_TIMEOUT_MS = 15000;

export async function lookupBarcodeEnrich(barcode, token, { timeoutMs = BARCODE_LOOKUP_TIMEOUT_MS } = {}) {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(
      `${baseUrl}/inventory/barcode-lookup/${encodeURIComponent(barcode)}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: controller.signal,
      },
    );
    const data = await response.json().catch(() => ({}));
    if (response.status === 403) throw new Error(data.error || 'forbidden');
    if (response.status === 400) throw new Error(data.error || 'invalid_barcode');
    if (!response.ok) throw new Error(data.error || `Request failed with status ${response.status}`);
    return data;
  } catch (err) {
    if (err.name === 'AbortError') {
      const timeoutErr = new Error('timeout');
      timeoutErr.name = 'AbortError';
      throw timeoutErr;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

function extractInventoryItems(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.products)) return data.products;
  if (Array.isArray(data.inventory)) return data.inventory;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

function extractNextCursor(data, requestCursor) {
  if (!data || Array.isArray(data)) return null;
  const raw = data.next_cursor
    ?? data.nextCursor
    ?? data.last_key
    ?? data.lastKey
    ?? data.pagination?.next_cursor
    ?? data.pagination?.nextCursor
    ?? data.cursor
    ?? null;
  if (raw === undefined || raw === null || raw === '') return null;
  const token = typeof raw === 'string' ? raw : JSON.stringify(raw);
  if (requestCursor && token === String(requestCursor)) return null;
  return raw;
}

export async function listProducts(token, {
  q, category, barcode, cursor, limit, includeInactive, incomplete, updatedSince,
} = {}) {
  const query = buildQuery({
    q, category, barcode, cursor, limit,
    include_inactive: includeInactive ? 1 : undefined,
    incomplete: incomplete ? 1 : undefined,
    updated_since: updatedSince || undefined,
  });
  const data = await apiFetch(`/inventory${query}`, { token });
  const items = extractInventoryItems(data);
  return { items: items.map(normalizeProduct), nextCursor: extractNextCursor(data, cursor) };
}

export async function listAllProducts(token, params = {}) {
  let cursor;
  const all = [];
  const seenIds = new Set();
  const seenCursors = new Set();
  const pageLimit = Number(params.limit) > 0 ? Number(params.limit) : 100;

  for (let page = 0; page < 50; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { items, nextCursor } = await listProducts(token, { ...params, cursor, limit: pageLimit });
    let added = 0;
    items.forEach((item) => {
      const id = item.id || item.sku;
      if (id && seenIds.has(id)) return;
      if (id) seenIds.add(id);
      all.push(item);
      added += 1;
    });

    if (nextCursor != null && nextCursor !== '') {
      const cursorKey = typeof nextCursor === 'string' ? nextCursor : JSON.stringify(nextCursor);
      if (!seenCursors.has(cursorKey)) {
        seenCursors.add(cursorKey);
        cursor = nextCursor;
        continue;
      }
    }

    // Some list endpoints omit next_cursor but still truncate to `limit`.
    if (items.length >= pageLimit && added > 0) {
      const lastId = items[items.length - 1]?.id;
      if (lastId && lastId !== cursor) {
        cursor = lastId;
        continue;
      }
    }
    break;
  }
  return all;
}

/** Catalog delta. Follows next_cursor only. Does not send include_inactive. */
export async function listProductsUpdatedSince(token, updatedSince) {
  if (!updatedSince) {
    const err = new Error('invalid_updated_since');
    err.code = 'invalid_updated_since';
    throw err;
  }
  let cursor;
  const all = [];
  const seenIds = new Set();
  const seenCursors = new Set();
  do {
    // eslint-disable-next-line no-await-in-loop
    const { items, nextCursor } = await listProducts(token, {
      updatedSince,
      cursor,
      limit: 100,
    });
    items.forEach((item) => {
      if (item.id && seenIds.has(item.id)) return;
      if (item.id) seenIds.add(item.id);
      all.push(item);
    });
    if (nextCursor == null || nextCursor === '') break;
    const cursorKey = typeof nextCursor === 'string' ? nextCursor : JSON.stringify(nextCursor);
    if (seenCursors.has(cursorKey)) break;
    seenCursors.add(cursorKey);
    cursor = nextCursor;
  } while (cursor);
  return all;
}

export async function getProduct(id, token) {
  const raw = await apiFetch(`/inventory/${encodeURIComponent(id)}`, { token });
  return normalizeProduct(raw.item || raw);
}

export async function createProduct(data, token) {
  const raw = await apiFetch('/inventory', { method: 'POST', token, body: toInventoryPayload(data) });
  return normalizeProduct(raw.item || raw);
}

export async function updateProduct(id, data, token) {
  const raw = await apiFetch(`/inventory/${id}`, { method: 'PATCH', token, body: toInventoryPayload(data) });
  return normalizeProduct(raw.item || raw);
}

/** PATCH only category slug — used when reassigning before category delete. */
export async function reassignProductCategory(id, categoryId, token) {
  const raw = await apiFetch(`/inventory/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    token,
    body: { category: categoryId },
  });
  return normalizeProduct(raw.item || raw);
}

/**
 * Stock in. Pass a number for legacy qty-only, or an options object:
 * { qty, selling_price, mrp?, unit_cost?, reason_code?, notes?, reference? }
 */
export function stockIn(id, qtyOrOpts, token, reason = 'Bulk onboarding') {
  const body = typeof qtyOrOpts === 'object' && qtyOrOpts !== null
    ? {
      qty: Number(qtyOrOpts.qty),
      selling_price: qtyOrOpts.selling_price !== undefined && qtyOrOpts.selling_price !== ''
        ? Number(qtyOrOpts.selling_price)
        : undefined,
      mrp: qtyOrOpts.mrp !== undefined && qtyOrOpts.mrp !== '' ? Number(qtyOrOpts.mrp) : undefined,
      unit_cost: qtyOrOpts.unit_cost !== undefined && qtyOrOpts.unit_cost !== ''
        ? Number(qtyOrOpts.unit_cost)
        : undefined,
      reason_code: qtyOrOpts.reason_code || undefined,
      notes: qtyOrOpts.notes || qtyOrOpts.reason || undefined,
      reference: qtyOrOpts.reference || undefined,
      reason: qtyOrOpts.reason || undefined,
    }
    : { qty: Number(qtyOrOpts), reason };
  Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);
  return apiFetch(`/inventory/${id}/stock-in`, { method: 'POST', token, body });
}

export function stockOut(id, { qty, lot_id, reason_code, notes } = {}, token) {
  const body = {
    qty: Number(qty),
    lot_id: lot_id || undefined,
    reason_code: reason_code || undefined,
    notes: notes || undefined,
  };
  Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);
  return apiFetch(`/inventory/${id}/stock-out`, { method: 'POST', token, body });
}

export function stockAdjust(id, {
  lot_id, qty_delta, absolute_qty, notes, reason_code,
} = {}, token) {
  const body = {
    lot_id: lot_id || undefined,
    notes: notes || undefined,
    reason_code: reason_code || undefined,
  };
  if (absolute_qty !== undefined && absolute_qty !== '') {
    body.absolute_qty = Number(absolute_qty);
  } else if (qty_delta !== undefined && qty_delta !== '') {
    body.qty_delta = Number(qty_delta);
  }
  Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);
  return apiFetch(`/inventory/${id}/stock-adjust`, { method: 'POST', token, body });
}

export async function onboardProduct(data, token) {
  const created = await createProduct({ ...data, stock: undefined, status: undefined }, token);
  if (Number(data.stock) > 0) {
    await stockIn(created.id, {
      qty: data.stock,
      selling_price: data.price !== undefined && data.price !== '' ? Number(data.price) : undefined,
      mrp: data.mrp !== undefined && data.mrp !== '' ? Number(data.mrp) : undefined,
      reason_code: 'PURCHASE',
      notes: 'Onboarding',
    }, token);
  }
  return updateProduct(created.id, { ...data, status: 'ACTIVE' }, token);
}

export async function onboardProductsBulk(items, token, { dryRun = false, onProgress } = {}) {
  const CHUNK_SIZE = 500;
  const allResults = [];

  for (let i = 0; i < items.length; i += CHUNK_SIZE) {
    const chunk = items.slice(i, i + CHUNK_SIZE);
    try {
      const rawRes = await bulkImportProducts(chunk, token, { dryRun, importImages: true });
      const parsedChunkResults = parseChunkResponse(rawRes, chunk);

      for (const res of parsedChunkResults) {
        const { item, ok, itemId, error } = res;
        if (!ok || dryRun) {
          const itemResult = { ok, error, status: dryRun ? (ok ? 'validated' : 'error') : (ok ? 'success' : 'error') };
          allResults.push({ item, result: itemResult });
          onProgress?.(item, itemResult);
          continue;
        }

        const filesToUpload = Array.isArray(item.localFiles) && item.localFiles.length
          ? item.localFiles.slice(0, 5)
          : (item.localFile ? [item.localFile] : []);

        if (filesToUpload.length > 0) {
          try {
            onProgress?.(item, { status: 'uploading_image', ok: true });
            const s3Keys = await uploadProductImages(itemId, filesToUpload, token);
            await updateProduct(itemId, { imageKeys: s3Keys }, token);
            const itemResult = { ok: true, status: 'success' };
            allResults.push({ item, result: itemResult });
            onProgress?.(item, itemResult);
          } catch (uploadErr) {
            const itemResult = {
              ok: false,
              status: 'error',
              error: `Product created, but image upload failed: ${uploadErr.message || 'S3 upload error'}`,
            };
            allResults.push({ item, result: itemResult });
            onProgress?.(item, itemResult);
          }
        } else {
          const itemResult = { ok: true, status: 'success' };
          allResults.push({ item, result: itemResult });
          onProgress?.(item, itemResult);
        }
      }
    } catch (chunkErr) {
      for (const item of chunk) {
        const itemResult = { ok: false, status: 'error', error: chunkErr.message || 'Bulk import request failed.' };
        allResults.push({ item, result: itemResult });
        onProgress?.(item, itemResult);
      }
    }
  }

  return allResults;
}

async function presignProductImage(id, contentType, token) {
  const raw = await apiFetch(`/inventory/${id}/images/presign`, {
    method: 'POST',
    token,
    body: { content_type: contentType },
  });
  return { uploadUrl: raw.upload_url, s3Key: raw.s3_key };
}

export async function uploadProductImage(id, file, token) {
  const { uploadUrl, s3Key } = await presignProductImage(id, file.type || 'image/jpeg', token);
  await putToPresignedUrl(uploadUrl, file);
  return s3Key;
}

export async function uploadProductImages(id, files, token) {
  const keys = [];
  for (const file of files) {
    // eslint-disable-next-line no-await-in-loop
    keys.push(await uploadProductImage(id, file, token));
  }
  return keys;
}

export async function deleteProductImage(id, s3Key, token) {
  await apiFetch(`/inventory/${id}/images`, { method: 'DELETE', token, body: { s3_key: s3Key } });
}

// Best-effort: the API doesn't echo s3_key back on read, only the resulting URL,
// so removing a pre-existing (already-uploaded) image infers its key from the URL path.
export function keyFromImageUrl(url) {
  try {
    return decodeURIComponent(new URL(url).pathname.replace(/^\//, ''));
  } catch {
    return url;
  }
}
